import React from 'react';
import { TFunction } from 'i18next';
import { ADMIN_PAGES_KEYS } from '../../../../../../../i18n/constants/keys';
import { NameEmailAvatar } from '../../../../../../../ui';
import ScoreStatus from '../Components/ScoreStatus';
import { IInterviewResult } from '../Hooks';

export interface TableColumn {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: any, index?: number) => JSX.Element;
  disableSort: boolean;
}

interface GetColumnsArgs {
  t: TFunction;
}

// -- TABLE COLUMNS -----------------------------------------------------------
export const getTableColumns = ({ t }: GetColumnsArgs): TableColumn[] => {
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const SKILL_FIELDS: Array<{ key: keyof IInterviewResult; label: string }> = [
    { key: 'technicalSkills', label: t(SI.results.columns.skills.technical) },
    { key: 'roleExperience', label: t(SI.results.columns.skills.experience) },
    { key: 'industrySkills', label: t(SI.results.columns.skills.industry) },
    { key: 'productKnowledge', label: t(SI.results.columns.skills.product) },
    { key: 'communication', label: t(SI.results.columns.skills.communication) },
    { key: 'problemSolving', label: t(SI.results.columns.skills.problemSolving) },
    { key: 'leadership', label: t(SI.results.columns.skills.leadership) },
    { key: 'teamCollaboration', label: t(SI.results.columns.skills.collaboration) },
  ];
  return [
    {
      key: 'user',
      label: t(SI.results.columns.applicant),
      width: '300px',
      align: 'left',
      disableSort: false,
      render: (row) => (
        <NameEmailAvatar
            email={row.user?.email}
            name={`${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`}
            avatar={''}
        />
      ),
    },
    {
      key: 'interviewStartTime',
      label: t(SI.results.columns.startTime),
      width: '200px',
      align: 'left',
      disableSort: false,
      render: (row) => (
        <span>{ new Date(row.interviewStartTime * 1000).toLocaleString() }</span>
      ),
    },
    {
      key: 'interviewType',
      label: t(SI.results.columns.type),
      width: '150px',
      align: 'left',
      disableSort: false,
      render: (row) => <span className='capitalize'>{ row.interviewType }</span>,
    },
    {
      key: 'interviewApplicantStatus',
      label: t(SI.results.columns.applicantStatus),
      width: '180px',
      align: 'left',
      disableSort: false,
      render: (row) => (
        <span className='capitalize'>{ row.interviewApplicantStatus }</span>
      ),
    },
    {
      key: 'status',
      label: t(SI.results.columns.status),
      width: '150px',
      align: 'left',
      disableSort: false,
      render: (row) => <span className='capitalize'>{ row.status }</span>,
    },
    {
      key: 'aiOverallScore',
      label: t(SI.results.columns.aiScore),
      width: '150px',
      align: 'left',
      disableSort: false,
      render: (row: any) => <ScoreStatus score={row.aiOverallScore?.score} />,
    },

    ...SKILL_FIELDS.map(({ key, label }) => ({
      key,
      label,
      width: '120px',
      align: 'left' as const,
      disableSort: false,
      render: (row: any) => <ScoreStatus score={row[key]?.score} />,
    })),
  ];
};

// -- GRID COLUMNS ------------------------------------------------------------
export const getGridColumns = ({ t }: GetColumnsArgs): TableColumn[] => {
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const SKILL_FIELDS: Array<{ key: keyof IInterviewResult; label: string }> = [
    { key: 'technicalSkills', label: t(SI.results.columns.skills.technical) },
    { key: 'roleExperience', label: t(SI.results.columns.skills.experience) },
    { key: 'industrySkills', label: t(SI.results.columns.skills.industry) },
    { key: 'productKnowledge', label: t(SI.results.columns.skills.product) },
    { key: 'communication', label: t(SI.results.columns.skills.communication) },
    { key: 'problemSolving', label: t(SI.results.columns.skills.problemSolving) },
    { key: 'leadership', label: t(SI.results.columns.skills.leadership) },
    { key: 'teamCollaboration', label: t(SI.results.columns.skills.collaboration) },
  ];
  return [
    {
      key: 'user',
      label: t(SI.results.columns.applicant),
      disableSort: false,
      render: (row) => (
        <NameEmailAvatar
            email={row.user?.email}
            name={`${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`}
            avatar={''}
        />
      ),
    },

    {
      key: 'aiOverallScore',
      label: t(SI.results.columns.aiScore),
      disableSort: false,
      render: (row: any) => <ScoreStatus score={row.aiOverallScore?.score} />,
    },
    ...SKILL_FIELDS.map(({ key, label }) => ({
      key,
      label,
      disableSort: false,
      render: (row: any) => <ScoreStatus score={row[key]?.score} />,
    })),
  ];
};
